/* eslint-disable max-lines */
import {
  LeaseState,
  LeaseResponse,
  LeaseResponseStatus,
  KickoutResponseStatus,
  Lease,
  MultiLicense,
} from '@common/license/types';
import type { getLeaseFromApi, liberateLease, returnLeaseToApi, refreshLoginToKickLease } from './leaseApi';
import { deleteLeaseToken, getLeaseToken, saveLeaseToken } from './leaseToken';
import { contains, pluck } from 'ramda';
import type { Store } from 'vuex';

export const minTimeout = 120 * 1000; // minimal timeout 2 minutes
const maxTimeout = 2147483647; // setTimeout uses 32 bit signed integer https://stackoverflow.com/a/14859062

const containsSelfOccupiedSeat = (messages: Array<{ name: string }>): boolean =>
  contains('atleast_one_seat_occupied_same_platform', pluck('name')(messages));

const isLeaseNotExpired = (lease: Lease) => {
  const now = new Date();
  const expirationDate = new Date(lease.expiration_at);

  return now < expirationDate;
};

export class LeaseManager {
  private requestLeaseRetryAttempt = 0;
  private renewTimeout = null;
  private requestLeaseRetryTimeout: ReturnType<typeof setTimeout> = null;
  private gettingLeaseFromMultiLicense = false;
  private kickLeasePromise: Promise<KickoutResponseStatus> = null;
  private leaseRequestPromise: ReturnType<typeof getLeaseFromApi> = null;
  private retryInProgress: Promise<void> = Promise.resolve();

  constructor(
    private store: Store<any>,
    private leaseApi: {
      getLeaseFromApi: typeof getLeaseFromApi;
      liberateLease: typeof liberateLease;
      returnLeaseToApi: typeof returnLeaseToApi;
      refreshLoginToKickLease: typeof refreshLoginToKickLease;
    }
  ) {}

  async getLease(ignoreCache: boolean = false) {
    if (!ignoreCache && this.isLeaseValid()) {
      return this.store.state.user.lease;
    }

    if (!this.leaseRequestPromise) {
      // Return lease request that is in progress
      this.leaseRequestPromise = this.leaseApi.getLeaseFromApi();
    }

    const leaseResponse = await this.leaseRequestPromise;

    this.leaseRequestPromise = null;

    return this.evaluateResponse(leaseResponse);
  }

  private async pickLicense(data: MultiLicense) {
    const poolId = data.options[0].id;
    // eslint-disable-next-line camelcase
    const aLease = await this.leaseApi.getLeaseFromApi({ preferred_pool_id: poolId });

    return aLease;
  }

  private async evaluateResponse(response: LeaseResponse) {
    let lease: Lease = null;

    this.resetRetryCounterIfApplicable(response.status);

    switch (response.status) {
      case LeaseResponseStatus.LeaseOk:
        lease = saveLeaseToken(response.leaseToken);
        this.storeLeaseData(LeaseState.Ok, lease);
        this.scheduleLeaseRenewalAt(lease.renew_at, lease.issued_at);
        break;
      case LeaseResponseStatus.MultiLicense:
        lease = await this.handleMultiLicense(response);
        break;
      case LeaseResponseStatus.NoMatchingLicense:
        this.storeLeaseData(LeaseState.NoMatchingLicense);
        break;
      case LeaseResponseStatus.LicenseConflict:
        this.handleLicenseConflict(response);
        break;
      case LeaseResponseStatus.Unauthorized:
        // should not happen
        // TODO:handle unauthorized?
        break;
      case LeaseResponseStatus.NoAccount:
        // User has no ACO acocunt. Case should not happen live.
        // Handle it like a server error but don't retry either
        this.storeLeaseData(LeaseState.ServerUnavailable);
        break;
      case LeaseResponseStatus.LeaseError:
      case LeaseResponseStatus.NoAnswer:
      case LeaseResponseStatus.ServerError:
        this.storeLeaseData(LeaseState.ServerUnavailable);
        this.retryGetLease();
        break;
      default:
        break;
    }

    return lease;
  }

  isLeaseValid() {
    if (!this.store.state.user.lease) {
      return false;
    }

    return isLeaseNotExpired(this.store.state.user.lease);
  }

  /**
   *
   * Returns true
   * - if active lease is unexpired,
   * - if no active lease exists
   */
  hasNoLeaseOrUnexpiredLease() {
    if (this.store.state.user.leaseState === LeaseState.Ok) {
      return isLeaseNotExpired(this.store.state.user.lease);
    }

    return true;
  }

  private resetRetryCounterIfApplicable(status: LeaseResponseStatus) {
    if (
      !(
        status === LeaseResponseStatus.NoAnswer ||
        status === LeaseResponseStatus.ServerError ||
        status === LeaseResponseStatus.LicenseConflict
      )
    ) {
      this.resetRetryCounter();
    }
  }

  private resetRetryCounter() {
    this.requestLeaseRetryAttempt = 0;
  }

  private async retryGetLease() {
    // Only allow a single retry "loop"
    // (If the lease is requested from multiple places, every failed lease request triggers a retry)
    await this.retryInProgress;

    this.retryInProgress = new Promise((resolve) => {
      this.requestLeaseRetryTimeout = setTimeout(async () => {
        await this.getLease(true);

        resolve();
      }, Math.min(Math.pow(2, this.requestLeaseRetryAttempt) * 30000, maxTimeout));
    });

    this.requestLeaseRetryAttempt++;
  }

  private scheduleLeaseRenewalAt(renewAt: string, issuedAt: string) {
    const renewAtDate = new Date(renewAt);
    const issuedAtDate = new Date(issuedAt);
    /**
     * If the current time is in the past (e.g. OS time was set manually),
     * use the issuedAt time as starting point. Otherwise, the renewAt time
     * will be too far in the future.
     */
    const referenceTime = Math.max(Date.now(), issuedAtDate.getTime());

    const timeout = Math.min(Math.max(renewAtDate.getTime() - referenceTime, minTimeout), maxTimeout);

    if (!isNaN(timeout)) {
      this.renewTimeout = setTimeout(() => {
        this.getLease(true);
      }, timeout);
    }
  }

  storeLeaseData(leaseState: LeaseState, lease: Lease = null) {
    this.store.dispatch('user/setLeaseData', { leaseState, lease });
  }

  async handleMultiLicense(response: LeaseResponse) {
    let lease = null;
    let multiResponse = null;

    // gettingLeaseFromMultiLicense protects against possible infinite loop
    if (!this.gettingLeaseFromMultiLicense) {
      this.gettingLeaseFromMultiLicense = true;
      multiResponse = await this.pickLicense(response.data);
      lease = this.evaluateResponse(multiResponse);
      this.gettingLeaseFromMultiLicense = false;
    }

    return lease;
  }

  handleLicenseConflict(response: LeaseResponse) {
    if (response.data?.statusMessages && containsSelfOccupiedSeat(response.data.statusMessages)) {
      this.storeLeaseData(LeaseState.SelfOccupiedSeat);
    } else {
      // HTTP 409 means no free seats (API info is misleading)
      this.storeLeaseData(LeaseState.NoFreeSeats);
      this.retryGetLease();
    }
  }

  /**
   * Returns the lease token from cache or requests it if not available yet.
   * A "null-token", i.e. no matching license is considered a successful lease-request response
   */
  async requestLeaseToken() {
    if (this.hasLeaseResponse() && this.hasNoLeaseOrUnexpiredLease()) {
      return getLeaseToken();
    }

    if (this.kickLeasePromise) {
      await this.kickLeasePromise;
    }

    await this.getLease();

    return getLeaseToken();
  }

  /**
   * Blocks a lease for certain duration.
   * @param duration - duration in minutes
   */
  async blockLease(duration: number) {
    // eslint-disable-next-line camelcase
    const leaseResponse: LeaseResponse = await this.leaseApi.getLeaseFromApi({ expected_lease_time: duration });

    return this.evaluateResponse(leaseResponse);
  }

  /**
   * Unblock a previously blocked lease
   */
  async unblockLease() {
    // setting expected_lease_time to a low value resets the lease time to default
    // eslint-disable-next-line camelcase
    const leaseResponse: LeaseResponse = await this.leaseApi.getLeaseFromApi({ expected_lease_time: -1 });

    return this.evaluateResponse(leaseResponse);
  }

  async returnLease() {
    if (this.store.state.user.lease && !this.store.state.user.lease.extended_expiration_at) {
      await this.leaseApi.returnLeaseToApi(this.store.state.user.lease.id);
    }

    deleteLeaseToken();
    this.storeLeaseData(LeaseState.Undefined);

    if (this.renewTimeout) {
      clearTimeout(this.renewTimeout);
    }

    if (this.requestLeaseRetryTimeout) {
      clearTimeout(this.requestLeaseRetryTimeout);
    }
  }

  hasLeaseResponse() {
    if (this.store.state.user.leaseState === LeaseState.Undefined) {
      return false;
    }

    return true;
  }

  /**
   * Kick an unreachable lease (i.e. applicationIdentifier was lost)
   */
  async kickLease() {
    if (!this.store.state.user.kickLeaseInProgress) {
      this.store.commit('user/USER_KICK_LEASE_STARTED');
      this.kickLeasePromise = this.leaseApi.liberateLease();
      const retVal: KickoutResponseStatus = await this.kickLeasePromise;
      this.kickLeasePromise = null;
      this.store.commit('user/USER_KICK_LEASE_COMPLETED');

      switch (retVal) {
        case KickoutResponseStatus.Success:
          this.getLease(true);
          break;
        case KickoutResponseStatus.NoLeaseFound:
          this.getLease(true);
          break;
        case KickoutResponseStatus.LoginNeedsRefresh:
          // refreshLogin to retry
          this.leaseApi.refreshLoginToKickLease();
          break;
        case KickoutResponseStatus.ServerError:
        case KickoutResponseStatus.NoAnswer:
          this.retryGetLease();
          break;
        default:
          break;
      }
    }
  }
}
